import api from '../core/api';

export interface FormFieldDef {
  key: string;
  label: string;
  type: string;
  required?: boolean;
  placeholder?: string;
  options?: { label: string; value: string }[];
  masterType?: string;
  multiple?: boolean;
  validation?: Record<string, unknown>;
  sortOrder?: number;
}

export interface FormDefinition {
  _id: string;
  code: string;
  name: string;
  entityType: string;
  module?: string;
  fields: FormFieldDef[];
  settings?: Record<string, unknown>;
  isActive: boolean;
}

export async function getFormByCode(code: string) {
  const res = await api.get(`/runtime/forms/${code}`);
  return res.data.data as FormDefinition;
}

export async function submitDynamicForm(formCode: string, data: Record<string, unknown>, status = 'submitted') {
  const res = await api.post('/dynamic/forms/submit', { formCode, data, status });
  return res.data.data;
}

export interface EntityDefinition {
  serviceCode: string;
  entityKey: string;
  label: string;
  description?: string;
  icon: string;
  viewType: 'table' | 'form' | 'master' | 'hybrid';
  formCode?: string;
  masterType?: string;
  apiPath?: string;
  columns: { field: string; headerName: string; width?: number; flex?: number }[];
}

export async function getEntityDefinition(serviceCode: string, entityKey: string) {
  const res = await api.get(`/runtime/entities/${serviceCode}/${entityKey}`);
  return res.data.data as EntityDefinition;
}

export async function listEntityDefinitions(serviceCode?: string) {
  const res = await api.get('/runtime/entities', { params: { serviceCode } });
  return res.data.data as EntityDefinition[];
}

export interface DashboardWidget {
  id: string;
  type: 'stat' | 'chart_line' | 'chart_bar' | 'table' | 'list' | 'map';
  title: string;
  metricKey?: string;
  apiPath?: string;
  config?: Record<string, unknown>;
}

export interface DashboardLayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface DashboardDefinition {
  _id?: string;
  code: string;
  name: string;
  description?: string;
  scope?: 'platform' | 'tenant' | 'role' | 'service';
  scopeValue?: string;
  widgets: DashboardWidget[];
  layout: DashboardLayoutItem[];
  isDefault?: boolean;
}

export async function getDefaultDashboard() {
  const res = await api.get('/runtime/dashboards/default');
  return res.data.data as DashboardDefinition | null;
}

export async function getDashboardForRole(role: string) {
  const res = await api.get(`/runtime/dashboards/role/${role}`);
  return res.data.data as DashboardDefinition;
}

export async function listDashboards() {
  const res = await api.get('/runtime/dashboards');
  return res.data.data as DashboardDefinition[];
}

export async function saveDashboard(payload: DashboardDefinition) {
  const res = await api.put('/runtime/dashboards', payload);
  return res.data.data as DashboardDefinition;
}

export interface MasterDefinitionGroup {
  _id: string;
  key: string;
  label: string;
  description?: string;
  icon: string;
  module?: string;
  serviceCode?: string;
  isCommon: boolean;
  types: {
    key: string;
    label: string;
    description?: string;
    module?: string;
    supportsHierarchy?: boolean;
    geoFields?: string[];
    metadataFields?: Record<string, unknown>[];
  }[];
  sortOrder: number;
  isActive: boolean;
}

export async function listMasterGroups() {
  const res = await api.get('/runtime/master-groups');
  return res.data.data as MasterDefinitionGroup[];
}

export async function saveMasterGroup(payload: Partial<MasterDefinitionGroup>, key?: string) {
  const res = key
    ? await api.put(`/runtime/master-groups/${key}`, payload)
    : await api.post('/runtime/master-groups', payload);
  return res.data.data as MasterDefinitionGroup;
}

export async function deleteMasterGroup(key: string) {
  const res = await api.delete(`/runtime/master-groups/${key}`);
  return res.data.data;
}

export async function syncMasterGroups() {
  const res = await api.post('/runtime/master-groups/sync');
  return res.data.data as MasterDefinitionGroup[];
}
